/**
 * format.js — Boothly number + date formatting utilities
 *
 * All formatters use German locale (de-DE) and are null-safe.
 * Import what you need; do not add dependencies.
 */

const LOCALE = "de-DE";
// Geschütztes Leerzeichen (U+00A0) — hält Betrag + Einheit (€/k€/%) als unteilbare Einheit
// zusammen, damit z.B. "5.294,51" und "€" nie auf zwei Zeilen umbrechen.
const NBSP = String.fromCharCode(0x00a0);

// ── Number formatting ────────────────────────────────────────────────────────

/**
 * Format a number with 2 decimal places (German locale), no currency sign.
 * Example: fmt2(12.5) → "12,50"
 */
export function fmt2(n) {
  return (n || 0).toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Format a number as Euro currency string.
 * Returns "—" for null/undefined/NaN.
 * Betrag + Euro-Zeichen sind eine unteilbare Einheit → geschütztes Leerzeichen ( ),
 * damit "12,50" und "€" nie auf zwei Zeilen umbrechen.
 * Example: fmtCurrency(12.5) → "12,50 €"
 */
export function fmtCurrency(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + NBSP + "€";
}

/**
 * Compact currency: uses "k€" suffix for values >= 1000.
 * Geschütztes Leerzeichen vor k€/€ (kein Umbruch zwischen Zahl und Einheit).
 * Example: fmtCurrencyCompact(1500) → "1,5 k€"
 */
export function fmtCurrencyCompact(n) {
  const v = Number(n) || 0;
  if (v >= 1000) {
    return (v / 1000).toLocaleString(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + NBSP + "k€";
  }
  return v.toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + NBSP + "€";
}

/**
 * Compact integer euros with German thousands separator, symbol in front.
 * Rounds to whole euros (no cents). For dashboard stat tiles + chart values.
 * Example: fmtEuroInt(187) → "€187", fmtEuroInt(1240.6) → "€1.241"
 */
export function fmtEuroInt(n) {
  return "€" + Math.round(Number(n) || 0).toLocaleString(LOCALE);
}

/**
 * Format a decimal number with a fixed number of fraction digits.
 * Use for percentages, rates, and other non-currency decimals.
 * Returns "—" for null/undefined/NaN.
 * Example: fmtDecimal(3.14159, 1) → "3.1"
 */
export function fmtDecimal(n, digits = 2) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  return Number(n).toFixed(digits);
}

/**
 * Format a number as a percentage string (German locale, comma separator).
 * Returns "—" for null/undefined/NaN. Caller passes the percent value (not a ratio).
 * Example: fmtPercent(48.333) → "48,3 %"
 */
export function fmtPercent(n, digits = 1) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString(LOCALE, { minimumFractionDigits: digits, maximumFractionDigits: digits }) + ' %';
}

// ── Date formatting ──────────────────────────────────────────────────────────

/**
 * Format ISO string as "DD.MM.YYYY".
 * Example: fmtDate("2026-05-15T10:00:00Z") → "15.05.2026"
 */
export function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(LOCALE, { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * Format ISO string as "DD.MM." (day + month, no year).
 * Example: fmtDateShort("2026-05-15T10:00:00Z") → "15.05."
 */
export function fmtDateShort(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(LOCALE, { day: "2-digit", month: "2-digit" });
}

/**
 * Format ISO string as "5. Mai" (day + abbreviated month).
 * Example: fmtDayMonth("2026-05-15T10:00:00Z") → "15. Mai"
 */
export function fmtDayMonth(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(LOCALE, { day: "numeric", month: "short" });
}

/**
 * Format ISO string as "Mai 2026" (month + year).
 * Example: fmtMonth("2026-05-15T10:00:00Z") → "Mai 2026"
 */
export function fmtMonth(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(LOCALE, { month: "long", year: "numeric" });
}

/**
 * Format ISO string as "HH:MM".
 * Example: fmtTime("2026-05-15T10:30:00Z") → "12:30" (CET)
 */
export function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(LOCALE, { hour: "2-digit", minute: "2-digit" });
}

/**
 * Format today's date as "Montag, 15. Januar" (for landing/header use).
 * Pass an optional ISO string; defaults to now.
 */
export function fmtDateLong(iso) {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleDateString(LOCALE, { weekday: "long", day: "numeric", month: "long" });
}

/**
 * Format a 2-digit-year short date for display in small spaces.
 * Example: fmtDateTiny("2026-05-15") → "15.05.26"
 */
export function fmtDateTiny(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(LOCALE, { day: "2-digit", month: "2-digit", year: "2-digit" });
}
